import { useEffect, useRef } from 'react';
import { PAN_CULL_IDLE_MS } from './constants';
import { calculateVisibleBounds } from './geometry';

/**
 * Recalc visibility when tasks/positions change, the window or container
 * resizes, or the camera settles. A live pan only moves the canvas transform.
 * New cards mount on a later turn.
 */
export function useVirtualizationEffects({
  containerRef,
  updateVisibility,
  followCamera,
  tasks,
  positionsToUse,
  panRef,
  zoomRef,
  containerRectRef,
  visibleBoundsRef,
  setGesturing,
}) {
  const panCullIdleRef = useRef(null);

  useEffect(() => {
    updateVisibility(true);
  }, [tasks, positionsToUse, updateVisibility]);

  useEffect(() => {
    const handleResize = () => updateVisibility(true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updateVisibility]);

  // Keep viewport size fresh without reading layout during every pan frame
  useEffect(() => {
    const el = containerRef?.current;
    if (!el) return undefined;

    const writeSize = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) {
        containerRectRef.current = { width: r.width, height: r.height };
      }
    };

    writeSize();
    const ro = new ResizeObserver(writeSize);
    ro.observe(el);
    return () => ro.disconnect();
  }, [containerRef, containerRectRef]);

  // Listen for pan/zoom updates from direct DOM manipulation.
  // The pan frame only stores the camera. Catch-up mounts cards afterward.
  // Idle culls anything the drag carried off screen.
  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    let canvas = container.querySelector('.flowchart__canvas');
    let retryTimeout = null;

    const flushVisibility = () => {
      if (panCullIdleRef.current) {
        clearTimeout(panCullIdleRef.current);
        panCullIdleRef.current = null;
      }
      setGesturing?.(false);
      updateVisibility(true);
    };

    const handlePanUpdate = (e) => {
      const { x, y, zoom: newZoom } = e.detail;
      panRef.current = { x, y };
      zoomRef.current = newZoom;
      setGesturing?.(true);
      followCamera?.();

      const rect = containerRectRef.current;
      if (rect?.width && rect?.height) {
        visibleBoundsRef.current = calculateVisibleBounds(rect, panRef.current, newZoom);
      }

      if (panCullIdleRef.current) clearTimeout(panCullIdleRef.current);
      panCullIdleRef.current = setTimeout(flushVisibility, PAN_CULL_IDLE_MS);
    };

    const attachListener = () => {
      canvas = container.querySelector('.flowchart__canvas');
      if (canvas) {
        canvas.addEventListener('panupdate', handlePanUpdate);
        return true;
      }
      return false;
    };

    if (!attachListener()) {
      retryTimeout = setTimeout(() => {
        attachListener();
      }, 100);
    }

    window.addEventListener('flowchartpanend', flushVisibility);

    return () => {
      if (retryTimeout) clearTimeout(retryTimeout);
      if (panCullIdleRef.current) {
        clearTimeout(panCullIdleRef.current);
        panCullIdleRef.current = null;
      }
      window.removeEventListener('flowchartpanend', flushVisibility);
      if (canvas) canvas.removeEventListener('panupdate', handlePanUpdate);
    };
  }, [containerRef, updateVisibility, followCamera, panRef, zoomRef, containerRectRef, visibleBoundsRef, setGesturing]);
}
